import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useQuery } from '@tanstack/react-query';
import { Receipt } from '@phosphor-icons/react';
import { getBillingSettings, getStripeAccount } from '@/api/billing';
import { listMyOrganizations } from '@/api/orgs';
import { AccessDenied } from '@/components/AccessDenied';
import { Badge } from '@/components/ui/badge';
import { BillingInvoices } from './settings/BillingInvoices';

// Top-level Billing page at /orgs/:slug/billing. Lifts the invoice list
// + editor out of the settings/billing tab so it's a first-class nav
// destination. Settings/billing keeps the configuration + Stripe Connect
// onboarding (less-frequent ops, stays where it is).
//
// Owner / administrator only — leaders / parents see a friendly
// "no permission" surface (the backend enforces the same rule).
export function Billing() {
  const { slug = '' } = useParams();
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });
  const org = orgsQuery.data?.find((o) => o.slug === slug);
  const role = org?.role ?? '';
  const canManage = role === 'owner' || role === 'administrator';

  const settingsQuery = useQuery({
    queryKey: ['billing-settings', slug],
    queryFn: () => getBillingSettings(token, slug),
    enabled: !!token && canManage,
  });
  const accountQuery = useQuery({
    queryKey: ['billing-stripe-account', slug],
    queryFn: () => getStripeAccount(token, slug),
    enabled: !!token && canManage,
  });

  if (!canManage) {
    return <AccessDenied />;
  }

  // Surface a discoverable banner when Stripe isn't connected — the user
  // can't do anything useful here without it. Link points at the existing
  // settings-billing Stripe panel.
  const stripeReady =
    accountQuery.data?.connected && accountQuery.data.charges_enabled;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 p-4 md:p-8">
      <header className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold tracking-tight md:text-3xl">
            <Receipt weight="bold" className="h-6 w-6 shrink-0" />
            {t('nav.billing')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('billing.invoices.subtitle', {
              defaultValue: 'Drafts edit freely; issued invoices are immutable.',
            })}
          </p>
        </div>
        {settingsQuery.data?.currency_locked && (
          <Badge variant="secondary">{settingsQuery.data.currency}</Badge>
        )}
      </header>

      {!stripeReady && (
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">
            {t('billing.notReadyBanner.title', {
              defaultValue: 'Stripe Connect not finished',
            })}
          </p>
          <p className="mt-1">
            {t('billing.notReadyBanner.body', {
              defaultValue:
                'You can draft invoices, but customers cannot pay until your Stripe account is fully verified.',
            })}{' '}
            <a className="underline" href={`/orgs/${slug}/settings/billing`}>
              {t('billing.notReadyBanner.cta', { defaultValue: 'Open Stripe settings →' })}
            </a>
          </p>
        </div>
      )}

      <BillingInvoices
        slug={slug}
        token={token}
        lockedCurrency={settingsQuery.data?.currency ?? ''}
        defaultCurrency="SEK"
      />
    </div>
  );
}
